import React, { useState } from 'react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { EmptyState } from '../../components/layout/SharedComponents';
import api, { getApiErrorMessage } from '../../services/api';
import { toast } from 'sonner';
import { RefreshCw, Shield, Trash2, Truck, RotateCcw, Hash } from 'lucide-react';
import { SortableHeader, sortData, toggleSort } from './sortUtils';

export const BATCH_STATUS_STYLES = {
  ACTIVE: 'bg-emerald-100 text-emerald-700',
  EXPIRING: 'bg-amber-100 text-amber-700',
  EXPIRED: 'bg-red-100 text-red-700',
  QUARANTINED: 'bg-orange-100 text-orange-700',
  PENDING_WRITEOFF: 'bg-purple-100 text-purple-700',
  WRITTEN_OFF: 'bg-slate-100 text-slate-500',
  RETURNED_TO_SUPPLIER: 'bg-cyan-100 text-cyan-700',
};

export default function BatchLifecycleTab({ expiringData, batchStatusFilter, setBatchStatusFilter, loadExpiring, onItemClick, onSerialDrillDown, fmtNum }) {
  const [actionBatch, setActionBatch] = useState(null);
  const [actionType, setActionType] = useState('');
  const [reason, setReason] = useState('');
  const [disposition, setDisposition] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [batchSort, setBatchSort] = useState({ key: '', dir: 'asc' });

  const counts = expiringData?.counts || {};

  const filters = [
    { id: 'expiring', label: 'Expiring Soon', count: counts.expiring, color: 'text-amber-700 bg-amber-50 border-amber-200' },
    { id: 'expired', label: 'Expired', count: counts.expired, color: 'text-red-700 bg-red-50 border-red-200' },
    { id: 'quarantined', label: 'Quarantined', count: counts.quarantined, color: 'text-orange-700 bg-orange-50 border-orange-200' },
    { id: 'disposed', label: 'Disposed', count: counts.disposed, color: 'text-slate-600 bg-slate-50 border-slate-200' },
  ];

  const submitQuarantine = async () => {
    if (!actionBatch) return;
    setSubmitting(true);
    try {
      const r = await api.post(`/api/inventory/batches/${actionBatch.batch_number}/quarantine`, { reason: reason || 'Expired stock' });
      toast.success(r.data.message, { duration: 6000 });
      setActionBatch(null);
      setReason('');
      loadExpiring();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed')); }
    setSubmitting(false);
  };

  const submitDispose = async () => {
    if (!actionBatch || !disposition) return;
    setSubmitting(true);
    try {
      const r = await api.post(`/api/inventory/batches/${actionBatch.batch_number}/dispose`, { disposition, reason });
      toast.success(r.data.message, { duration: 8000 });
      setActionBatch(null);
      setReason('');
      setDisposition('');
      loadExpiring();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed')); }
    setSubmitting(false);
  };

  return (
    <div className="space-y-4" data-testid="batch-lifecycle-tab">
      {/* Filter chips */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          {filters.map(f => (
            <button key={f.id} onClick={() => setBatchStatusFilter(f.id)}
              data-testid={`batch-filter-${f.id}`}
              className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${batchStatusFilter === f.id ? `${f.color} ring-2 ring-offset-1 ring-primary/30` : 'border-border text-muted-foreground hover:bg-accent/20'}`}>
              {f.label}
              {f.count > 0 && <span className="ml-1.5 font-bold">{f.count}</span>}
            </button>
          ))}
        </div>
        <Button size="sm" variant="outline" onClick={loadExpiring}>
          <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
        </Button>
      </div>

      {/* Data table */}
      {!expiringData || expiringData.total === 0 ? (
        <EmptyState title={`No ${batchStatusFilter} batches`} description={
          batchStatusFilter === 'expiring' ? 'No batches expiring in the next 60 days.' :
          batchStatusFilter === 'expired' ? 'No expired batches. Run Auto-Expire Scan to check.' :
          batchStatusFilter === 'quarantined' ? 'No quarantined batches.' :
          'No disposed batches.'
        } />
      ) : (
        <div className="border rounded-xl overflow-hidden">
          <table className="w-full text-xs" data-testid="batch-lifecycle-table">
            <thead><tr className="bg-muted/50 border-b">
              <SortableHeader label="Item" sortKey="item_code" currentSort={batchSort} onSort={toggleSort(setBatchSort)} />
              <SortableHeader label="Batch #" sortKey="batch_number" currentSort={batchSort} onSort={toggleSort(setBatchSort)} />
              <SortableHeader label="Location" sortKey="location_code" currentSort={batchSort} onSort={toggleSort(setBatchSort)} />
              <SortableHeader label="Qty Remaining" sortKey="quantity_remaining" currentSort={batchSort} onSort={toggleSort(setBatchSort)} className="text-right" />
              <SortableHeader label="Expiry Date" sortKey="expiry_date" currentSort={batchSort} onSort={toggleSort(setBatchSort)} />
              <SortableHeader label="Status" sortKey="status" currentSort={batchSort} onSort={toggleSort(setBatchSort)} />
              <SortableHeader label="Manufacturer" sortKey="manufacturer" currentSort={batchSort} onSort={toggleSort(setBatchSort)} />
              <th className="text-center px-3 py-2 font-semibold">Actions</th>
            </tr></thead>
            <tbody>
              {sortData(expiringData.data, batchSort.key, batchSort.dir).map((b, i) => {
                const daysLeft = b.expiry_date ? Math.ceil((new Date(b.expiry_date) - new Date()) / (1000 * 60 * 60 * 24)) : null;
                const isExpired = daysLeft !== null && daysLeft <= 0;
                const isExpiringSoon = daysLeft !== null && daysLeft > 0 && daysLeft <= 7;
                const canQuarantine = ['ACTIVE', 'EXPIRING', 'EXPIRED'].includes(b.status) && (b.quantity_remaining || 0) > 0;
                const canDispose = ['QUARANTINED', 'EXPIRED', 'EXPIRING'].includes(b.status);
                const isTerminal = ['WRITTEN_OFF', 'RETURNED_TO_SUPPLIER', 'PENDING_WRITEOFF'].includes(b.status);

                return (
                  <tr key={`${b.batch_number}-${b.location_code}-${i}`}
                    className={`border-b hover:bg-accent/20 transition-colors ${isExpired && !isTerminal ? 'bg-red-50/50' : isExpiringSoon && !isTerminal ? 'bg-amber-50/30' : ''}`}
                    data-testid={`lifecycle-row-${b.batch_number}`}>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5">
                        <span className="font-semibold text-primary underline decoration-dotted underline-offset-2 cursor-pointer"
                          onClick={() => onItemClick(b.item_code)}>{b.item_code}</span>
                        <span className="text-muted-foreground">{b.item_description?.slice(0, 20)}</span>
                        {b.serial_tracked && (
                          <button onClick={() => onSerialDrillDown(b.item_code)}
                            className="ml-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-indigo-50 border border-indigo-200 text-indigo-700 text-[8px] font-semibold hover:bg-indigo-100 transition-colors"
                            title="View all serial numbers for this item"
                            data-testid={`view-serials-${b.item_code}`}>
                            <Hash className="h-2.5 w-2.5" /> Serials
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 font-mono font-semibold">{b.batch_number}</td>
                    <td className="px-3 py-2">{b.location_code}</td>
                    <td className="px-3 py-2 text-right font-semibold">{fmtNum(b.quantity_remaining)}</td>
                    <td className="px-3 py-2">
                      <span className={isExpired ? 'text-red-700 font-bold' : isExpiringSoon ? 'text-amber-700 font-semibold' : ''}>{b.expiry_date?.slice(0, 10) || '—'}</span>
                      {daysLeft !== null && <span className="text-muted-foreground ml-1">({daysLeft <= 0 ? `${Math.abs(daysLeft)}d ago` : `${daysLeft}d`})</span>}
                    </td>
                    <td className="px-3 py-2">
                      <Badge className={`text-[9px] ${BATCH_STATUS_STYLES[b.status] || 'bg-slate-100'}`}>
                        {b.status?.replace(/_/g, ' ')}
                      </Badge>
                    </td>
                    <td className="px-3 py-2">{b.manufacturer || '—'}</td>
                    <td className="px-3 py-1.5 text-center">
                      <div className="flex gap-1 justify-center">
                        {canQuarantine && (
                          <Button size="sm" variant="outline" onClick={() => { setActionBatch(b); setActionType('quarantine'); setReason('Expired stock'); }}
                            className="h-5 text-[9px] px-1.5 border-orange-300 text-orange-700 hover:bg-orange-50"
                            data-testid={`lifecycle-quarantine-${b.batch_number}`}>
                            <Shield className="h-2.5 w-2.5 mr-0.5" /> Quarantine
                          </Button>
                        )}
                        {canDispose && (
                          <Button size="sm" variant="outline" onClick={() => { setActionBatch(b); setActionType('dispose'); setDisposition(''); setReason(''); }}
                            className="h-5 text-[9px] px-1.5 border-purple-300 text-purple-700 hover:bg-purple-50"
                            data-testid={`lifecycle-dispose-${b.batch_number}`}>
                            Dispose
                          </Button>
                        )}
                        {isTerminal && <span className="text-[9px] text-muted-foreground italic">Closed</span>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Quarantine Dialog */}
      {actionBatch && actionType === 'quarantine' && (
        <Dialog open onOpenChange={() => setActionBatch(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle className="text-sm">Quarantine Batch {actionBatch.batch_number}</DialogTitle></DialogHeader>
            <p className="text-xs text-muted-foreground">This will remove the batch from available stock and place associated serial numbers on hold.</p>
            <div className="text-xs bg-muted/50 rounded-lg p-3 space-y-1">
              <p><span className="text-muted-foreground">Item:</span> <strong>{actionBatch.item_code}</strong></p>
              <p><span className="text-muted-foreground">Qty Remaining:</span> <strong>{actionBatch.quantity_remaining}</strong></p>
              <p><span className="text-muted-foreground">Location:</span> {actionBatch.location_code}</p>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold">Reason</Label>
              <Textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} className="text-xs" data-testid="lifecycle-quarantine-reason" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={() => setActionBatch(null)}>Cancel</Button>
              <Button size="sm" onClick={submitQuarantine} disabled={submitting} className="bg-orange-600 hover:bg-orange-700" data-testid="lifecycle-quarantine-confirm">
                {submitting ? 'Processing...' : 'Confirm Quarantine'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Dispose Dialog */}
      {actionBatch && actionType === 'dispose' && (
        <Dialog open onOpenChange={() => setActionBatch(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="text-sm">Dispose Batch {actionBatch.batch_number}</DialogTitle></DialogHeader>
            <div className="text-xs space-y-1 bg-muted/50 rounded-lg p-3">
              <p><span className="text-muted-foreground">Item:</span> <strong>{actionBatch.item_code}</strong></p>
              <p><span className="text-muted-foreground">Status:</span> <Badge className={`text-[9px] ${BATCH_STATUS_STYLES[actionBatch.status]}`}>{actionBatch.status}</Badge></p>
              <p><span className="text-muted-foreground">Qty Remaining:</span> <strong>{actionBatch.quantity_remaining}</strong></p>
              <p><span className="text-muted-foreground">Location:</span> {actionBatch.location_code}</p>
            </div>
            <div className="space-y-3 pt-1">
              <Label className="text-xs font-semibold">Disposition Action</Label>
              <div className="grid grid-cols-3 gap-2">
                <button onClick={() => setDisposition('release')}
                  className={`flex flex-col items-center gap-1 p-3 border rounded-lg text-xs transition-colors ${disposition === 'release' ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-300' : 'hover:bg-accent/20'}`}
                  data-testid="lifecycle-dispose-release">
                  <RotateCcw className="h-5 w-5 text-emerald-600" />
                  <span className="font-semibold text-emerald-700">Release</span>
                  <span className="text-[9px] text-muted-foreground text-center">Return to active stock</span>
                </button>
                <button onClick={() => setDisposition('write_off')}
                  className={`flex flex-col items-center gap-1 p-3 border rounded-lg text-xs transition-colors ${disposition === 'write_off' ? 'border-red-500 bg-red-50 ring-2 ring-red-300' : 'hover:bg-accent/20'}`}
                  data-testid="lifecycle-dispose-writeoff">
                  <Trash2 className="h-5 w-5 text-red-600" />
                  <span className="font-semibold text-red-700">Write Off</span>
                  <span className="text-[9px] text-muted-foreground text-center">Destroy / dispose</span>
                </button>
                <button onClick={() => setDisposition('return_to_supplier')}
                  className={`flex flex-col items-center gap-1 p-3 border rounded-lg text-xs transition-colors ${disposition === 'return_to_supplier' ? 'border-cyan-500 bg-cyan-50 ring-2 ring-cyan-300' : 'hover:bg-accent/20'}`}
                  data-testid="lifecycle-dispose-return">
                  <Truck className="h-5 w-5 text-cyan-600" />
                  <span className="font-semibold text-cyan-700">Return to Supplier</span>
                  <span className="text-[9px] text-muted-foreground text-center">Send back for credit</span>
                </button>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Reason / Notes</Label>
                <Textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} className="text-xs"
                  placeholder={disposition === 'release' ? 'QA approved, batch is safe...' : disposition === 'write_off' ? 'Expired beyond recovery...' : 'Supplier credit agreement ref...'}
                  data-testid="lifecycle-dispose-reason" />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={() => setActionBatch(null)}>Cancel</Button>
              <Button size="sm" onClick={submitDispose} disabled={submitting || !disposition}
                className={disposition === 'release' ? 'bg-emerald-600 hover:bg-emerald-700' : disposition === 'write_off' ? 'bg-red-600 hover:bg-red-700' : disposition === 'return_to_supplier' ? 'bg-cyan-600 hover:bg-cyan-700' : ''}
                data-testid="lifecycle-dispose-confirm">
                {submitting ? 'Processing...' : disposition === 'release' ? 'Release to Stock' : disposition === 'write_off' ? 'Submit for Approval' : disposition === 'return_to_supplier' ? 'Return to Supplier' : 'Select Action'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
